import { isStandardLoyaltyLocale, Locale } from '@headless-workspace/config';
import { DateHelpers } from '@headless-workspace/utils';
import { CustomerDTO } from './CustomerDTO';

export enum UserSephoraCard {
    PreBuy = 'PRE_ACHAT',
    White = 'WHITE',
    Black = 'BLACK',
    Gold = 'GOLD',
}

export enum UserSephoraCardUnlimited {
    Standard = 'STANDARD',
    Star = 'STAR',
}

export enum UserMySephoraCard {
    Bronze = 'BRONZE',
    Silver = 'SILVER',
    Gold = 'GOLD',
}

export type UserSephoraCardType = UserSephoraCard | UserSephoraCardUnlimited | UserMySephoraCard;

export const isMySephoraCard = (card: UserSephoraCardType): card is UserMySephoraCard => {
    return card === UserMySephoraCard.Bronze || card === UserMySephoraCard.Silver || card === UserMySephoraCard.Gold;
};

export const isSephoraCard = (card: UserSephoraCardType): card is UserSephoraCard => {
    return (
        card === UserSephoraCard.PreBuy ||
        card === UserSephoraCard.White ||
        card === UserSephoraCard.Black ||
        card === UserSephoraCard.Gold
    );
};

export const isSephoraUnlimitedCard = (card: UserSephoraCardType): card is UserSephoraCardUnlimited => {
    return card === UserSephoraCardUnlimited.Standard || card === UserSephoraCardUnlimited.Star;
};

type Loyalty = {
    currentPoints: number;
    pointsToNextDiscount: number;
    sephoraCard: UserSephoraCardType;
};

export type HighlightedOffer = {
    id?: string;
    title?: string;
    promoCode?: string;
};

export type UserValue = Readonly<{
    lastName: string;
    firstName: string;
    favoriteStoreId?: string;
    loyalty?: Loyalty;
    highlightedOffer?: HighlightedOffer;
    isSubscribedByEmail: boolean;
    ordersCount: number;
    gender?: string;
    type?: string;
    hasFavoriteAddress?: boolean;
    hasFavoriteCreditCard?: boolean;
    hasOffers?: boolean;
    inscriptionDate?: string;
}>;

export type CustomerValue = Readonly<
    {
        customerId: string;
        favoriteStoreId?: string;
    } & UserValue
>;

export const mapStatusToSephoraCard = (locale: Locale, status?: string): UserSephoraCardType | undefined => {
    if (!status) {
        return undefined;
    }

    if (isStandardLoyaltyLocale(locale)) {
        switch (status) {
            case 'WHITE':
                return UserSephoraCard.White;
            case 'BLACK':
                return UserSephoraCard.Black;
            case 'GOLD':
                // Here we use UserSephoraCard.Gold but it can also be UserMySephoraCard.Gold
                return UserSephoraCard.Gold;
            case 'BRONZE':
                return UserMySephoraCard.Bronze;
            case 'SILVER':
                return UserMySephoraCard.Silver;
            default:
                return undefined;
        }
    }

    switch (status) {
        case 'STANDARD':
            return UserSephoraCardUnlimited.Standard;
        case 'STAR':
            return UserSephoraCardUnlimited.Star;
        default:
            return undefined;
    }
};

const mapLoyalty = (loyalty: CustomerDTO['loyalty'], locale: Locale): Loyalty | undefined => {
    const sephoraCard = mapStatusToSephoraCard(locale, loyalty?.status);
    const hasLoyalty = !!loyalty && !!loyalty.status && loyalty.status !== UserSephoraCard.PreBuy && !!sephoraCard;

    if (!hasLoyalty) {
        return undefined;
    }

    return {
        currentPoints: loyalty.currentPoints,
        pointsToNextDiscount: loyalty.pointsToNextDiscount,
        sephoraCard,
    };
};

export const mapCustomer = (dto: CustomerDTO, locale: Locale): CustomerValue => {
    return {
        customerId: dto.customerId,
        firstName: dto.firstName,
        lastName: dto.lastName,
        favoriteStoreId: dto.favoriteStoreId,
        loyalty: mapLoyalty(dto.loyalty, locale),
        highlightedOffer: dto.highlightedOffer,
        isSubscribedByEmail: dto.isSubscribedByEmail ?? false,
        ordersCount: dto.ordersCount ?? 0,
        gender: dto.gender,
        type: dto.type,
        hasFavoriteAddress: dto.hasFavoriteAddress,
        hasFavoriteCreditCard: dto.hasFavoriteCreditCard,
        hasOffers: dto.hasOffers,
        inscriptionDate: DateHelpers.formatDateFromDMYToYMD(dto.creationDate),
    };
};
